import { legacyLocalLabel, relativeInstant, legacyUtcDate } from '../../utilities/legacyDatePresentation';
import { createApiError } from "../../utilities/apiError";
// UserRolesService.ts

import UserInfo, { GetTimeOffsByUserIdReq, ManagerRequest, RequestHistoryItem, RequestHistoryApiItem, GetAlllUserByRoleReq } from "../../interfaces/UserInfo";
import { Region } from "../../interfaces/identity/Region";
import { UpcomingEventsByDateReq, UpcommingMeetingDto } from "../../interfaces/identity/calendar/Events";
import { ApiResponse, PagedResults } from "../../interfaces/genericResponse/GenericResponse";
import { buildQuery } from "../../utilities/common/buildQueryParams";

// Function to fetch a user's role by their user id
export async function getUserInfoById(authToken: string): Promise<UserInfo | null> {

    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/users/userInfo`

    try {
        const response = await fetch(
            apiUrl,
            {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            }
        );

        if (!response.ok) {
            // Handle non-OK responses, e.g., 404 for not found
            console.error(`Server responded with status: ${response.status}`);
            return null;
        }

        const userData = await response.json();

        const userInfo: UserInfo = {
            id: userData.id,
            scid: userData.scId,
            firstName: userData.firstName,
            lastName: userData.lastName,
            displayName: userData.displayName,
            joinDate: userData.joinDate,
            organization: userData.organization,
            email: userData.email,
            regions: userData.regions,
            roles: userData.roles
            // Add other necessary properties
        };

        return userInfo;
    } catch (error) {
        // Handle network errors or other exceptions
        console.error("Error fetching task:", error);
        return null;
    }
}

export async function getAllUsersInOrganization(
    authToken: string,
    onlyActive: boolean = false
): Promise<UserInfo[] | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/users?onlyActive=${onlyActive}`



    try {
        const response = await fetch(
            apiUrl,
            {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            }
        );

        if (!response.ok) {
            // Handle non-OK responses, e.g., 404 for not found
            console.error(`Server responded with status: ${response.status}`);
            return null;
        }

        const userData = await response.json();

        // Map the role data to Role interface
        const userInfo: UserInfo[] = userData.map((user: any) => ({
            id: user.id,
            scid: user.scid,
            firstName: user.firstName,
            lastName: user.lastName,
            displayName: user.displayName,
            joinDate: user.joinDate,
            organization: user.organization,
            email: user.email,
            employeeRole: user.employeeRole,
            isParticipant: user.isParticipant === true || user.employeeRole?.toLowerCase() === 'participant'
            // Add other necessary properties
        }));

        return userInfo;
    } catch (error) {
        // Handle network errors or other exceptions
        console.error("Error fetching task:", error);
        return null;
    }
}

export async function getSuperAdminChatInitiationDirectory(
    authToken: string
): Promise<UserInfo[] | null> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/chat/initiation-directory`;

    try {
        const response = await fetch(apiUrl, {
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        });

        if (!response.ok) {
            console.error(`Server responded with status: ${response.status}`);
            return null;
        }

        const userData = await response.json();
        return userData.map((user: any) => ({
            id: user.id,
            scid: user.scId ?? null,
            firstName: user.firstName,
            lastName: user.lastName,
            displayName: user.displayName,
            joinDate: user.joinDate,
            organization: user.organization,
            email: user.email,
            roles: user.roles ?? [],
            regions: user.regions ?? [],
            employeeRole: user.employeeRole,
            isParticipant: user.isParticipant === true || user.employeeRole?.toLowerCase() === 'participant',
        }));
    } catch (error) {
        console.error("Error fetching the super-admin chat directory:", error);
        return null;
    }
}

export async function getUserInfoByUserId(
    userId: string,
    authToken: string
): Promise<UserInfo | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/users/${userId}`

    try {
        const response = await fetch(
            apiUrl,
            {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            }
        );

        if (!response.ok) {
            // Handle non-OK responses, e.g., 404 for not found
            console.error(`Server responded with status: ${response.status}`);
            return null;
        }

        const user = await response.json();

        // Map the role data to Role interface


        return user;
    } catch (error) {
        // Handle network errors or other exceptions
        console.error("Error fetching task:", error);
        return null;
    }
}

// Function to fetch all available regions
export async function getAllRegions(
    authToken: string,
    onlyActive: boolean = false // Added optional parameter with default value
): Promise<ApiResponse<Region[]>> {
    // Construct the URL with query parameters
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/users/regions?onlyActive=${onlyActive}`;

    try {
        const response = await fetch(
            apiUrl,
            {
                method: 'GET', // Explicitly stating method is good practice
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                }
            }
        );

        if (!response.ok) {
            // Handle HTTP errors (404, 500, etc.)
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to fetch regions');
        }

        const regionsData = await response.json();
        return regionsData;
    } catch (error) {
        console.error("Error in getAllRegions:", error);
        throw error;
    }
}

export async function loginWithBackend(idToken: string): Promise<any> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/Authentication/login`;

    try {
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
        });
        return await response.json();
    } catch (error: any) {
        console.error("Error during login with backend:", error);
        return { error: "Failed to communicate with the server." };
    }
}



// src/services/timeOffService.ts

interface TimeOffPayload {
    id: number; // 0 for new, actual id for update
    userId: string;
    typeOfLeave: number;
    comment?: string;
    isApproved: boolean;
    timeOffStartTime: string;
    timeOffEndTime: string;
    requestType: "FULL_DAY" | "PARTIAL_DAY";
}

interface ApiResponses {
    // Define the expected success response structure from your backend
    // For example:
    // success: boolean;
    // message?: string;
    // data?: any;
    [key: string]: any; // Or a more specific type
}

interface ApiErrorResponse {
    error: string;
    details?: any;
}

/**
 * Adds or updates a time-off request.
 * @param timeOffData - The data for the time-off request.
 * @param idToken - The authentication token for the user.
 * @returns A promise that resolves to the API response.
 */
export async function addUpdateTimeOff(
    timeOffData: TimeOffPayload,
    idToken: string
): Promise<ApiResponse<any>> {
    // The API endpoint URL from your backend specification
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/TimeOff/add-update-timeOff`;

    try {
        const response = await fetch(apiUrl, {
            method: 'POST', // Use POST for creating/updating data
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(timeOffData),
        });

        return await response.json();
    } catch (error: any) {
        console.error("Error during add/update time off:", error);
        throw error;
    }
}

// timeOffService.ts (or wherever your service functions are defined)

// You might need to define TimeOffEntry if you don't have it already
export interface TimeOffEntry {
    id: number;
    timeOffStartDate: string | null;
    timeOffEndDate: string | null;
    typeOfLeave: 'sick' | 'casual' | 'earned'; // Add other types as per your backend
    comment: string | null;
    isApproved: boolean;
    isRejected: boolean;
    rejectionReason: string | null;
    intelligenceSnapshotJson?: string | null;
    reviewRevision?: number;
    impactAcknowledgedRevision?: number | null;
    impactAcknowledgedAt?: string | null;
}

export async function acknowledgeLeaveImpact(id: number, reviewRevision: number, idToken: string): Promise<ApiResponse<any>> {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/TimeOff/${id}/impact-acknowledgement`, {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ reviewRevision }),
    });
    return response.json();
}


export async function getTimeOffsByUser(
    req: GetTimeOffsByUserIdReq,
    idToken: string
): Promise<ApiResponse<PagedResults<TimeOffEntry>>> {

    const baseUrl = `${process.env.REACT_APP_API_BASE_URL}/TimeOff/get-timeOffs-by-user`;

    // Convert req object → query string
    const queryParams = new URLSearchParams();

    Object.entries(req).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, value.toString());
        }
    });

    const finalUrl = `${baseUrl}?${queryParams.toString()}`;

    try {
        const response = await fetch(finalUrl, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
        });

        return await response.json();
    } catch (error: any) {
        console.error("Error during fetching time offs:", error);
        throw error;
    }
}


// timeOffService.ts (add this function)


// Assuming GetTimeOffsResponse and TimeOffEntry are already defined as in previous responses

export interface GetTimeOffByIdResponse extends ApiResponses {
    data: TimeOffEntry; // This API returns a single TimeOffEntry in 'data'
}

export async function getTimeOffById(
    timeOffId: number,
    idToken: string
): Promise<GetTimeOffByIdResponse | ApiErrorResponse> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/TimeOff/delete-timeOff/${timeOffId}`;

    try {
        const response = await fetch(apiUrl, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            let errorBody;
            try {
                errorBody = await response.json();
            } catch (e) {
                errorBody = { message: response.statusText };
            }
            console.error(`API Error Response for get-timeOff-by-id (${timeOffId}):`, errorBody);
            throw createApiError({
                status: response.status,
                message: `API request failed with status ${response.status}`,
                details: errorBody,
            });
        }

        return await response.json();
    } catch (error: any) {
        console.error(`Error during fetching time off by ID (${timeOffId}):`, error);
        if (error.status) {
            return { error: error.message, details: error.details };
        }
        return { error: "Failed to communicate with the server. Please check your network connection or the server status." };
    }
}





// Error type
export interface ApiError {
    error: string;
    details?: any;
}

export async function getTimeOffsForManager(
    managerId: string,
    idToken: string,
    transactionalTypeName: string = "",
    pageNumber: number = 1,
    pageSize: number = 10,
    status?: string,
    startDate?: string,
    endDate?: string
): Promise<ApiResponse<PagedResults<ManagerRequest>>> {
    const queryParams = new URLSearchParams();
    queryParams.append('transactionalTypeName', transactionalTypeName);
    queryParams.append('pageNumber', pageNumber.toString());
    queryParams.append('pageSize', pageSize.toString());
    queryParams.append('sortDesc', true.toString());

    if (status && status !== "All Status") {
        queryParams.append('status', status);
    }

    if (startDate && endDate) {
        queryParams.append('startDate', startDate);
        queryParams.append('endDate', endDate);
    }

    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/TimeOff/manager-leaves?${queryParams.toString()}`;



    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${idToken}`,
                "Content-Type": "application/json",
            },
        });

        const data = await response.json();
        console.log('Raw Data', data)
        return data;
    } catch (error: any) {
        throw error;
    }
}

export async function approveRejectLeave(
    id: number,
    action: "approve" | "reject",
    reason: string,
    idToken: string
): Promise<ApiResponse<string>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/TimeOff/approve-reject/${id}`;
    const body = { action, reason };

    const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
    });

    return await response.json();
}



export interface ManagerDetails {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    scID: string;
}

export async function getManagerDetailsByEmployeeId(
    employeeId: string,
    idToken: string
): Promise<ManagerDetails | null> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/TimeOff/manager-details/${employeeId}`;

    try {
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch manager details:", response.statusText);
            return null;
        }

        const result = await response.json();
        return result.data;
    } catch (error) {
        console.error("Error fetching manager details:", error);
        return null;
    }
}


export async function getLeaveTypes(
    idToken: string
): Promise<any[] | ApiErrorResponse> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/TimeOff/all`;

    try {
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            let errorBody;
            try {
                errorBody = await response.json();
            } catch (e) {
                errorBody = { message: response.statusText };
            }

            console.error("API Error Response for getLeaveTypes:", errorBody);
            throw createApiError({
                status: response.status,
                message: `API request failed with status ${response.status}`,
                details: errorBody,
            });
        }

        return await response.json();
    } catch (error: any) {
        console.error("Error fetching leave types:", error);
        if (error.status) {
            return { error: error.message, details: error.details };
        }
        return { error: "Failed to communicate with the server. Please check your network connection or the server status." };
    }
}

// services/calendarService.ts

export async function getUpcomingMeetings(userId: string, idToken: string): Promise<UpcommingMeetingDto[] | ApiErrorResponse> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/next-upcoming-events/${userId}`;

    try {
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const errorBody = await response.json();
            throw createApiError({
                status: response.status,
                message: `API request failed with status ${response.status}`,
                details: errorBody,
            });
        }

        return await response.json();
    } catch (error: any) {
        console.error("Error fetching upcoming meetings:", error);
        return error.status
            ? { error: error.message, details: error.details }
            : { error: "Network/server issue." };
    }
}

export function formatMeetingTime(utcDateString: Date): string {
    const localTime = new Date(legacyUtcDate(utcDateString));

    const formattedDate = legacyLocalLabel(localTime, "MMMM d, yyyy 'at' h:mm a");
    const relativeTime = relativeInstant(localTime); // e.g., "in 1 day", "2 hours ago"

    return `${formattedDate} ${relativeTime}`;
}

export async function getUpcomingMeetingsByDate(
    userId: string,
    authToken: string,
    date: string
): Promise<ApiResponse<UpcommingMeetingDto[]>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/upcoming/Events-by-date`;
    console.log("d : ", date);

    const payload: UpcomingEventsByDateReq = {
        userId: userId,
        eventDate: date
    }
    console.log("Datee : ", payload);

    try {
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorBody = await response.json();
            throw createApiError({
                status: response.status,
                message: `API request failed with status ${response.status}`,
                details: errorBody,
            });
        }

        const events = await response.json()
        console.log('EVENTS: ', events)
        return events
    } catch (error: any) {
        return error;
    }
}

export async function endMeeting(
    meetingId: string,
    authToken: string,
    deleteScope: string = "single"
): Promise<ApiResponse<any>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/CalendarEvent/DeleteEvent/${meetingId}?deleteScope=${deleteScope}`;

    try {
        const response = await fetch(apiUrl, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            const errorBody = await response.json();
            throw createApiError({
                status: response.status,
                message: `API request failed with status ${response.status}`,
                details: errorBody,
            });
        }

        const result = await response.json();
        console.log('Meeting ended successfully:', result);
        return result;
    } catch (error: any) {
        console.error("Error ending meeting:", error);
        return error;
    }
}

export async function getAllUsersInOrganizationbyId(
    authToken: string
): Promise<UserInfo[] | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/users/getalluserbyorgid`

    try {
        const response = await fetch(
            apiUrl,
            {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            }
        );

        if (!response.ok) {
            // Handle non-OK responses, e.g., 404 for not found
            console.error(`Server responded with status: ${response.status}`);
            return null;
        }

        const userData = await response.json();

        // Map the role data to Role interface
        const userInfo: UserInfo[] = userData.map((user: any) => ({
            id: user.id,
            scid: user.scid,
            firstName: user.firstName,
            lastName: user.lastName,
            displayName: user.displayName,
            joinDate: user.joinDate,
            organization: user.organization,
            email: user.email,
            roles: user.roles || [], // Map roles from API response
            regions: user.regions || [],
            participantCount: user.participantCount || 0,
            participants: user.participants || []
            // Map regions if available
            // Add other necessary properties
        }));

        return userInfo;
    } catch (error) {
        // Handle network errors or other exceptions
        console.error("Error fetching task:", error);
        return null;
    }
}

export async function getRequestHistory(
    idToken: string,
    pageNumber: number = 1,
    pageSize: number = 10,
    status?: string,
    startDate?: string,
    endDate?: string
): Promise<ApiResponse<PagedResults<RequestHistoryItem>>> {
    const queryParams = new URLSearchParams();
    queryParams.append('pageNumber', pageNumber.toString());
    queryParams.append('pageSize', pageSize.toString());
    queryParams.append('sortDesc', true.toString());

    if (status && status !== "All Status") {
        queryParams.append('status', status);
    }

    if (startDate && endDate) {
        queryParams.append('startDate', startDate);
        queryParams.append('endDate', endDate);
    }

    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/timeoff/request-history?${queryParams.toString()}`;

    try {
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const errorBody = await response.json();
            throw createApiError({
                status: response.status,
                message: `API request failed with status ${response.status}`,
                details: errorBody,
            });
        }

        const apiResponse: ApiResponse<PagedResults<RequestHistoryApiItem>> = await response.json();

        // Transform API response to UI-expected format
        if (apiResponse.success && apiResponse.data) {
            const transformedItems: RequestHistoryItem[] = apiResponse.data.items.map((item: RequestHistoryApiItem) => {
                if (item.historyType === "leave") {
                    // Transform leave type: nest time off fields into timeOffDetails
                    return {
                        id: item.id,
                        historyType: item.historyType,
                        requestTime: item.requestTime,
                        status: item.status,
                        employeeTimeOffId: item.id, // Use id as employeeTimeOffId for leave type
                        approverName: item.approverName || "",
                        timeOffDetails: {
                            id: item.id,
                            timeOffStartDate: item.timeOffStartDate || "",
                            timeOffEndDate: item.timeOffEndDate || "",
                            typeOfLeave: item.typeOfLeave || "",
                            comment: item.comment || "",
                            isApproved: item.isApproved || false,
                            isRejected: item.isRejected || false,
                        },
                    };
                } else {
                    // Transform admin type: keep flat structure
                    return {
                        id: item.id,
                        historyType: item.historyType,
                        requestTime: item.requestTime,
                        status: item.status,
                        requesterName: item.requesterName,
                        transactionType: item.transactionType,
                        transactionTypeId: item.transactionTypeId,
                        taskId: item.taskId,
                        taskFriendlyId: item.friendlyTaskId,
                        taskTitle: item.taskTitle,
                        transactionReasonId: item.transactionReasonId,
                        transactionReasonText: item.transactionReasonText,
                        isTrainingTask: item.isTrainingTask === true,
                    };
                }
            });

            return {
                ...apiResponse,
                data: {
                    ...apiResponse.data,
                    items: transformedItems,
                },
            };
        }

        return apiResponse as ApiResponse<PagedResults<RequestHistoryItem>>;
    } catch (error: any) {
        console.error("Error fetching request history:", error);
        throw error;
    }
}

export async function GetAlllUserByRoleAsync(
    authToken: string,
    payload: GetAlllUserByRoleReq,
    signal?: AbortSignal
): Promise<ApiResponse<PagedResults<UserInfo>>> {
    const queryString = buildQuery(payload);
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/users/getAllUsersByRoles?${queryString}`;

    const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        signal,
    });

    return await response.json();
}
